import React, { useEffect, useMemo, useState } from 'react';
import { useUser, useAuth, UserButton } from '@clerk/clerk-react';
import viitumMark from '../assets/viitum-mark.png';
import { useAccount, useConnect, useDisconnect, useChainId, useWalletClient, useSwitchChain } from 'wagmi';
import { useAppKit } from '@reown/appkit/react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  IoWalletOutline,
  IoCopyOutline,
  IoCheckmarkCircle,
  IoTimeOutline,
  IoCloseCircle,
  IoRefreshOutline,
  IoLogoUsd,
  IoChevronDown,
  IoChevronUp,
  IoOpenOutline,
  IoAddCircleOutline,
  IoCheckmarkOutline,
} from 'react-icons/io5';
import presaleV2Service, { setPresaleV2AuthGetter } from '../services/presaleV2Service';
import DepositCard from './DepositCard';
import PresaleCalculator from './PresaleCalculator';
import { chainConfig, BSC_CHAIN_ID } from '../config/chains';
import './PresaleDashboard.css';

const CHAIN_META = {
  ethereum: { label: 'Ethereum', explorer: chainConfig('ethereum').explorer },
  bsc: { label: 'BSC', explorer: chainConfig('bsc').explorer },
  tron: { label: 'TRON', explorer: chainConfig('tron').explorer },
};

const truncateAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '');

function formatAmount(v, digits = 2) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

function useCountdown(targetDate) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!targetDate) return null;
  const target = new Date(targetDate).getTime();
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return { days, hours, mins, secs, done: diff === 0 };
}

function StatusBadge({ status }) {
  const cls = {
    confirmed: 'badge-green',
    sent: 'badge-green',
    pending: 'badge-yellow',
    processing: 'badge-yellow',
    failed: 'badge-red',
    refunded: 'badge-gray',
  }[status] || 'badge-gray';
  return <span className={`pv2-badge ${cls}`}>{status}</span>;
}

function PresaleInfoBar({ info }) {
  const activeRound = info?.activeRound;
  const countdown = info?.countdown;
  const target = countdown
    ? (new Date(countdown.startDate) > new Date() ? countdown.startDate : countdown.endDate)
    : null;
  const cd = useCountdown(target);
  const isStarting = countdown && new Date(countdown.startDate) > new Date();

  const percent = activeRound?.percentSold ?? 0;
  const sold = activeRound?.tokensSold ?? 0;
  const total = activeRound?.totalTokens ?? 0;
  const price = activeRound?.tokenPrice;

  return (
    <section className="pv2-card pv2-info-bar">
      <div className="pv2-info-top">
        <div>
          <div className="pv2-token-name">VIIT Presale</div>
          {activeRound ? (
            <div className="pv2-round-name">{activeRound.name}</div>
          ) : (
            <div className="pv2-round-name pv2-muted">
              {info?.isPresaleActive ? 'Presale starting soon' : 'Presale not active'}
            </div>
          )}
        </div>
        {price && (
          <div className="pv2-price">
            <span className="pv2-muted">Price</span>
            <strong>${formatAmount(price, 6)}</strong>
          </div>
        )}
      </div>

      {activeRound && (
        <>
          <div className="pv2-progress">
            <div className="pv2-progress-bar">
              <div className="pv2-progress-fill" style={{ width: `${Math.min(100, percent)}%` }} />
            </div>
            <div className="pv2-progress-labels">
              <span>{formatAmount(sold)} / {formatAmount(total)} VIIT</span>
              <span>{percent.toFixed(2)}% sold</span>
            </div>
          </div>

          {cd && (
            <div className="pv2-countdown">
              <IoTimeOutline />
              <span>{isStarting ? 'Starts in' : 'Ends in'}</span>
              <div className="pv2-countdown-cells">
                <div><b>{cd.days}</b><span>d</span></div>
                <div><b>{cd.hours}</b><span>h</span></div>
                <div><b>{cd.mins}</b><span>m</span></div>
                <div><b>{cd.secs}</b><span>s</span></div>
              </div>
            </div>
          )}
        </>
      )}

      {Array.isArray(info?.rounds) && info.rounds.length > 1 && (
        <div className="pv2-rounds-table-wrap">
          <table className="pv2-rounds-table">
            <thead>
              <tr><th>Round</th><th>Price</th><th>Allocation</th><th>Status</th></tr>
            </thead>
            <tbody>
              {info.rounds.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>${formatAmount(r.price, 6)}</td>
                  <td>{formatAmount(r.allocation)}</td>
                  <td><StatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function WalletCard({ savedWallet, onWalletConnected, onError }) {
  const { address, isConnected } = useAccount();
  const { isPending } = useConnect();
  const { disconnect } = useDisconnect();
  const chainId = useChainId();
  const { open: openAppKit } = useAppKit();

  const wrongChain = isConnected && chainId !== BSC_CHAIN_ID;
  const activeChainName = chainId === BSC_CHAIN_ID ? 'BSC' : 'Ethereum';

  // Open AppKit's built-in Networks modal rather than firing a custom switch.
  // Over a WalletConnect session (Trust Wallet mobile) the modal performs the
  // session-aware switch and re-prompts the wallet correctly, and it lets the
  // user move between Ethereum and BSC in either direction from one picker.
  const handleOpenNetworks = () => openAppKit({ view: 'Networks' });

  const connectMutation = useMutation({
    mutationFn: (addr) => presaleV2Service.connectWallet(addr),
    onSuccess: (res) => onWalletConnected?.(res.bscWalletAddress),
    onError: (err) => onError?.(err?.response?.data?.message || err?.message || 'Failed to save wallet'),
  });

  useEffect(() => {
    if (isConnected && address) {
      if (!savedWallet || savedWallet.toLowerCase() !== address.toLowerCase()) {
        connectMutation.mutate(address);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, address]);

  if (!isConnected) {
    return (
      <section className="pv2-card pv2-wallet-card">
        <h2><IoWalletOutline /> Connect your wallet</h2>
        <p className="pv2-muted">
          Connect a BSC wallet to receive your VIIT tokens. MetaMask, Trust Wallet,
          Rainbow, Coinbase Wallet, and any other WalletConnect-compatible wallet are supported.
        </p>
        <div className="pv2-connect-buttons">
          <button
            className="pv2-btn pv2-btn-primary"
            disabled={isPending}
            onClick={() => openAppKit({ view: 'Connect' })}
          >
            {isPending ? 'Connecting…' : 'Connect Wallet'}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="pv2-card pv2-wallet-card">
      <div className="pv2-wallet-header">
        <div>
          <div className="pv2-muted">Connected wallet</div>
          <div className="pv2-wallet-address">
            <span className="pv2-identicon" style={{ background: `linear-gradient(135deg, #${address.slice(2,8)}, #${address.slice(-6)})` }} />
            <code>{truncateAddr(address)}</code>
          </div>
        </div>
        <div className="pv2-wallet-network">
          <span className="pv2-network-badge">{activeChainName}</span>
          <button
            className="pv2-btn pv2-btn-ghost pv2-btn-sm"
            onClick={handleOpenNetworks}
          >
            Switch network
          </button>
        </div>
        <button className="pv2-btn pv2-btn-ghost" onClick={() => disconnect()}>Disconnect</button>
      </div>

      {wrongChain && (
        <div className="pv2-info-banner">
          <span>
            VIIT will be delivered on BSC at this same wallet address. Send USDT on the BEP-20 (BSC) network,
            or switch to BSC now to view your VIIT after delivery.
          </span>
          <button
            className="pv2-btn pv2-btn-secondary"
            onClick={handleOpenNetworks}
          >
            Switch to BSC
          </button>
        </div>
      )}

      {connectMutation.isError && (
        <div className="pv2-error-banner">
          {connectMutation.error?.response?.data?.message || connectMutation.error?.message}
        </div>
      )}
    </section>
  );
}

function DepositTabs({ addresses, activeRound }) {
  const [active, setActive] = useState('bsc');
  const byChain = useMemo(() => {
    const m = { ethereum: null, bsc: null, tron: null };
    for (const a of addresses || []) m[a.chain] = a;
    return m;
  }, [addresses]);
  const current = byChain[active];
  // Deposits are accepted on BEP-20 (BSC) only for now. ETH (ERC-20) and TRON
  // (TRC-20) tabs are intentionally disabled (commented out, not removed) so
  // they can be re-enabled by restoring them to this list.
  const tabs = [/* 'ethereum', */ 'bsc' /* , 'tron' */];

  return (
    <section className="pv2-card">
      <h2>Deposit USDT</h2>
      <div className="pv2-tabs">
        {tabs.map((c) => (
          <button
            key={c}
            className={`pv2-tab ${active === c ? 'active' : ''}`}
            onClick={() => setActive(c)}
          >
            {CHAIN_META[c].label}
          </button>
        ))}
      </div>
      <DepositCard
        chain={active}
        address={current?.address}
        networkName={current?.networkName || CHAIN_META[active].label}
        usdtContractAddress={current?.usdtContractAddress}
        warningText={current?.warning}
        calculator={
          activeRound?.tokenPrice ? (
            <PresaleCalculator
              tokenPrice={activeRound.tokenPrice}
              minPurchase={activeRound.minPurchase}
              maxPurchase={activeRound.maxPurchase}
            />
          ) : null
        }
      />
    </section>
  );
}

// VIIT is a BEP-20 token — importing it lets the wallet display the delivered
// balance. wallet_watchAsset only works on EVM wallets (MetaMask, Trust, etc.);
// the token must be added while the wallet is on BSC, so we switch first.
function AddViitToWallet({ viitAddress, hasBalance }) {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { data: walletClient } = useWalletClient();
  const { switchChainAsync } = useSwitchChain();
  // null = idle, 'pending' = request in flight, 'added' = succeeded,
  // 'unsupported' = wallet rejected the method or never responded (many mobile
  // wallets over WalletConnect don't expose watchAsset and simply hang).
  const [status, setStatus] = useState(null);
  const [copied, setCopied] = useState(false);

  const onBsc = chainId === BSC_CHAIN_ID;

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(viitAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard unavailable */ }
  };

  const addToken = async ({ silent = false } = {}) => {
    if (!walletClient || !viitAddress) return;
    if (!silent) setStatus('pending');
    try {
      if (!onBsc) {
        if (silent) return; // never force a network switch silently
        await switchChainAsync({ chainId: BSC_CHAIN_ID });
      }
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('watchAsset timeout')), 8000));
      const ok = await Promise.race([
        walletClient.watchAsset({
          type: 'ERC20',
          options: { address: viitAddress, symbol: 'VIIT', decimals: 18 },
        }),
        timeout,
      ]);
      if (!silent) setStatus(ok === false ? 'unsupported' : 'added');
    } catch (err) {
      if (silent) return;
      const code = err?.code ?? err?.cause?.code;
      const msg = String(err?.message || '').toLowerCase();
      // User rejections (or a declined chain switch) leave the UI idle — no noise.
      if (code === 4001 || msg.includes('user rejected') || msg.includes('user denied')) {
        setStatus(null);
        return;
      }
      setStatus('unsupported');
    }
  };

  // Auto-prompt once, the first time this wallet has a delivered VIIT balance,
  // and only if the wallet is already on BSC (never force a switch silently).
  // Persisted in localStorage so it doesn't re-prompt on every reload.
  const autoKey = address && viitAddress
    ? `viitum:autoAddedViit:${viitAddress.toLowerCase()}:${address.toLowerCase()}`
    : null;
  useEffect(() => {
    if (!hasBalance || !walletClient || !autoKey || !onBsc) return;
    if (localStorage.getItem(autoKey)) return;
    localStorage.setItem(autoKey, '1');
    addToken({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBalance, walletClient, autoKey, onBsc]);

  if (!isConnected || !viitAddress) return null;

  return (
    <div className="pv2-add-token">
      <button
        className="pv2-btn pv2-btn-secondary"
        onClick={() => addToken()}
        disabled={status === 'pending'}
      >
        <IoAddCircleOutline /> {status === 'pending' ? 'Check your wallet…' : 'Add VIIT to your wallet'}
      </button>
      {status === 'added' && (
        <span className="pv2-add-token-note pv2-add-token-ok">
          <IoCheckmarkCircle /> VIIT added to your wallet
        </span>
      )}
      {/* Many mobile wallets can't do one-tap add, so always show the address
          for manual import (copy → paste into the wallet's "add token" screen). */}
      <div className="pv2-add-token-manual">
        <span className="pv2-add-token-note">
          {status === 'unsupported'
            ? "Your wallet doesn't support one-tap add — import VIIT manually on BSC (BEP-20):"
            : "Not working on your wallet? Copy the VIIT contract address and import it manually on BSC (BEP-20):"}
        </span>
        <div className="pv2-add-token-addr">
          <code>{viitAddress}</code>
          <button
            type="button"
            className="pv2-btn pv2-btn-ghost pv2-btn-sm pv2-add-token-copy"
            onClick={copyAddress}
            aria-label="Copy VIIT contract address"
          >
            {copied ? <IoCheckmarkOutline /> : <IoCopyOutline />}
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function BalanceCard({ balance, viitAddress }) {
  const pending = Number(balance?.pendingViit || 0);
  const delivered = Number(balance?.totalViit || 0);
  return (
    <section className={`pv2-card pv2-balance-card ${pending > 0 ? 'pv2-has-pending' : ''}`}>
      <div className="pv2-muted">Your VIIT Balance</div>
      <div className="pv2-balance-big">{formatAmount(balance?.totalViit, 4)} <small>VIIT</small></div>
      {pending > 0 && (
        <div className="pv2-pending">
          <IoRefreshOutline className="pv2-spin-slow" />
          Pending: {formatAmount(pending, 4)} VIIT (being sent)
        </div>
      )}
      <div className="pv2-balance-meta">
        <div><IoLogoUsd /> Total USDT deposited: <strong>${formatAmount(balance?.totalUsdtDeposited, 2)}</strong></div>
        {balance?.bscWalletAddress && (
          <div className="pv2-muted">Delivery wallet: <code>{truncateAddr(balance.bscWalletAddress)}</code></div>
        )}
      </div>
      <AddViitToWallet viitAddress={viitAddress} hasBalance={delivered > 0} />
    </section>
  );
}

function HowToBuy({ content }) {
  const [open, setOpen] = useState(false);
  const steps = [
    'Sign up and log in',
    'Connect your BSC wallet (MetaMask or Trust Wallet)',
    'Copy the deposit address or scan the QR code',
    'Open Binance → Withdraw → Select USDT → Choose network → Paste address → Send',
    'Wait for confirmation (usually 2–5 minutes)',
    'VIIT tokens appear in your wallet automatically',
  ];
  return (
    <section className="pv2-card">
      <button className="pv2-accordion-header" onClick={() => setOpen((v) => !v)}>
        <span>How to buy</span>
        {open ? <IoChevronUp /> : <IoChevronDown />}
      </button>
      {open && (
        <div className="pv2-accordion-body">
          <ol className="pv2-steps">
            {steps.map((s, i) => (
              <li key={i}><span className="pv2-step-num">{i + 1}</span><span>{s}</span></li>
            ))}
          </ol>
          {content && (
            <div className="pv2-html-content" dangerouslySetInnerHTML={{ __html: content }} />
          )}
        </div>
      )}
    </section>
  );
}

function TxHistory({ entries }) {
  const [limit, setLimit] = useState(10);
  const items = entries || [];
  const visible = items.slice(0, limit);
  return (
    <section className="pv2-card">
      <h2>Transaction history</h2>
      {visible.length === 0 ? (
        <p className="pv2-muted">No transactions yet.</p>
      ) : (
        <div className="pv2-table-wrap">
          <table className="pv2-tx-table">
            <thead>
              <tr><th>Date</th><th>Type</th><th>Chain</th><th>Amount</th><th>Status</th><th>Tx</th></tr>
            </thead>
            <tbody>
              {visible.map((e, i) => (
                <tr key={i}>
                  <td>{new Date(e.timestamp).toLocaleString()}</td>
                  <td>
                    {e.type === 'deposit'
                      ? <><IoLogoUsd /> Deposit</>
                      : <><IoCheckmarkCircle /> Distribution</>}
                  </td>
                  <td>{CHAIN_META[e.chain]?.label || e.chain}</td>
                  <td>{formatAmount(e.amount, 4)}</td>
                  <td><StatusBadge status={e.status} /></td>
                  <td>
                    {e.txHash ? (
                      <a href={e.explorerUrl || '#'} target="_blank" rel="noreferrer">
                        {truncateAddr(e.txHash)} <IoOpenOutline />
                      </a>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {items.length > limit && (
        <button className="pv2-btn pv2-btn-ghost pv2-load-more" onClick={() => setLimit((n) => n + 10)}>
          Load more
        </button>
      )}
    </section>
  );
}

export default function PresaleDashboard() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const [registered, setRegistered] = useState(false);
  const [errorBanner, setErrorBanner] = useState('');

  useEffect(() => {
    setPresaleV2AuthGetter(getToken);
    return () => setPresaleV2AuthGetter(null);
  }, [getToken]);

  useEffect(() => {
    if (!user || registered) return;
    presaleV2Service.registerUser(user.id, user.primaryEmailAddress?.emailAddress)
      .then(() => setRegistered(true))
      .catch((e) => setErrorBanner(e?.response?.data?.message || e?.message || 'Registration failed'));
  }, [user, registered]);

  const infoQuery = useQuery({
    queryKey: ['presaleV2', 'info'],
    queryFn: presaleV2Service.getPresaleInfo,
    refetchInterval: 30000,
  });

  const addressesQuery = useQuery({
    queryKey: ['presaleV2', 'deposit-addresses'],
    queryFn: presaleV2Service.getDepositAddresses,
    enabled: registered,
  });

  const balanceQuery = useQuery({
    queryKey: ['presaleV2', 'balance'],
    queryFn: presaleV2Service.getBalance,
    enabled: registered,
    refetchInterval: 15000,
  });

  const historyQuery = useQuery({
    queryKey: ['presaleV2', 'tx-history'],
    queryFn: presaleV2Service.getTxHistory,
    enabled: registered,
    refetchInterval: 15000,
  });

  const { address: connectedAddress, isConnected } = useAccount();
  const savedWallet = balanceQuery.data?.bscWalletAddress;
  const walletReady = isConnected && !!connectedAddress;
  const presaleStarted = !!infoQuery.data?.activeRound;

  return (
    <div className="pv2-dashboard">
      <div className="pv2-container">
        <header className="pv2-header">
          <a href="/" className="pv2-header-brand">
            <img src={viitumMark} alt="VIITUM" />
            <span>VIITUM</span>
          </a>
          <div className="pv2-header-right">
            <a href="/" className="pv2-header-back">← Back to home</a>
            <UserButton
              afterSignOutUrl="/"
              appearance={{
                variables: {
                  colorBackground: '#1B1B26',
                  colorText: '#F5F5F7',
                  colorTextSecondary: '#9A9AA8',
                  colorPrimary: '#A52668',
                  colorInputBackground: '#14141C',
                  colorInputText: '#F5F5F7',
                },
                elements: {
                  userButtonPopoverCard: { background: '#1B1B26', border: '1px solid rgba(255,255,255,0.12)' },
                  userButtonPopoverMain: { background: '#1B1B26' },
                  userButtonPopoverActions: { background: '#1B1B26' },
                  userButtonPopoverFooter: { background: '#14141C' },
                  userButtonPopoverActionButton: { color: '#F5F5F7' },
                  userButtonPopoverActionButtonText: { color: '#F5F5F7' },
                  userButtonPopoverActionButtonIcon: { color: '#F5F5F7' },
                  userPreviewMainIdentifier: { color: '#F5F5F7' },
                  userPreviewSecondaryIdentifier: { color: '#9A9AA8' },
                },
              }}
            />
          </div>
        </header>

        {errorBanner && <div className="pv2-error-banner">{errorBanner}</div>}

        <PresaleInfoBar info={infoQuery.data} />

        <WalletCard
          savedWallet={savedWallet}
          onWalletConnected={() => balanceQuery.refetch()}
          onError={setErrorBanner}
        />

        {presaleStarted && walletReady && (
          <DepositTabs
            addresses={addressesQuery.data?.depositAddresses}
            activeRound={infoQuery.data?.activeRound}
          />
        )}

        {presaleStarted && (
          <BalanceCard
            balance={balanceQuery.data}
            viitAddress={infoQuery.data?.activeRound?.viitContractAddress}
          />
        )}

        <HowToBuy content={infoQuery.data?.howToBuyContent} />

        <TxHistory entries={historyQuery.data?.history} />
      </div>
    </div>
  );
}
